import { useDispatch, useSelector } from 'react-redux';

import {
  findCatalogEntry,
  useModelStrategyCatalog,
} from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import {
  clearLanguageModelOverride,
  setLanguageModelOverride,
} from '@/modules/tryIt/redux/actions';
import { selectLanguageModelOverride } from '@/modules/tryIt/redux/selectors';
import { ModelStrategyStep } from '@/shared/types/modelStrategy';

const emptyOverride = (): ModelStrategyStep => ({
  language_model_id: null,
  language_model_alias: null,
  reasoning_effort: null,
});

const hasAnyValue = (override: ModelStrategyStep) =>
  !!override.language_model_id || !!override.language_model_alias || !!override.reasoning_effort;

/**
 * Try It model-override state behind `ModelPanel`. `defaultModel` seeds the
 * picker when no override is set; selecting anything still creates an override.
 * The override state itself still lives in the `tryIt` redux slice, which the
 * replay panels share — move it into this module once nothing else reads it.
 */
export const useModelOverride = (
  brainId: string | undefined,
  defaultModel?: ModelStrategyStep | null
) => {
  const dispatch = useDispatch();
  const { buckets } = useModelStrategyCatalog(brainId);
  const override = useSelector(selectLanguageModelOverride);

  const pickerValue = override ?? defaultModel ?? emptyOverride();
  const isOverrideActive = !!override;
  const supportsReasoning = findCatalogEntry(buckets, pickerValue)?.supports_reasoning ?? false;

  const updateOverride = (next: Partial<ModelStrategyStep>) => {
    const merged = { ...pickerValue, ...next };
    // Drop a stale reasoning value when the picked model can't reason.
    const supports = findCatalogEntry(buckets, merged)?.supports_reasoning ?? false;
    const normalized = supports ? merged : { ...merged, reasoning_effort: null };
    dispatch(
      hasAnyValue(normalized) ? setLanguageModelOverride(normalized) : clearLanguageModelOverride()
    );
  };

  const clearOverride = () => dispatch(clearLanguageModelOverride());

  return {
    buckets,
    pickerValue,
    isOverrideActive,
    supportsReasoning,
    updateOverride,
    clearOverride,
  };
};
